import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useDemoPreview } from "@/contexts/DemoPreviewContext";
import { useLang } from "@/i18n";
import { Eye, RefreshCw, ShieldAlert, Mail, MessageSquare, Smartphone, BellRing, X, Copy } from "lucide-react";
import { toast } from "sonner";

// Phase D2 — Demo Preview Activity Log.
// Read-only forensic surface for admins. Lists recent demo sessions
// with summary counts (actions, blocked writes, intercepted comms) and
// lets the admin drill into a single session to inspect the full
// timeline. Strictly admin/staff via backend RBAC.

const CHANNEL_ICONS = {
  email: Mail,
  sms: MessageSquare,
  whatsapp: Smartphone,
  push: BellRing,
};

const channelLabel = (t, channel) => t(`admin.demoActivityLog.channels.${channel}`) || channel;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const DemoActivityLog = () => {
  const { t } = useLang();
  const { cloneSession } = useDemoPreview();
  const [sessions, setSessions] = useState([]);
  const [intercepted, setIntercepted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [channelFilter, setChannelFilter] = useState("");
  // D3-Phase-C — "only sessions with sandbox writes" toggle.
  // Backed by `?min_sandbox_writes=1` on the recent-sessions endpoint
  // so the filter applies server-side and the result count is exact.
  const [onlySandbox, setOnlySandbox] = useState(false);
  // Cosmetic block (Feb 2026) — track which row is currently being
  // cloned so we can show a per-row spinner without freezing the page.
  const [cloningId, setCloningId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const minSandbox = onlySandbox ? 1 : 0;
      const [recentRes, commsRes] = await Promise.all([
        api.get(`/admin/demo-preview/sessions/recent?limit=50&min_sandbox_writes=${minSandbox}`),
        api.get(`/admin/demo-preview/intercepted-comms?limit=100${channelFilter ? `&channel=${channelFilter}` : ""}`),
      ]);
      setSessions(recentRes.data?.items || []);
      setIntercepted(commsRes.data?.items || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("admin.demoActivityLog.errors.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [channelFilter, onlySandbox, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openDetail = useCallback(async (sessionId) => {
    setSelectedId(sessionId);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await api.get(`/admin/demo-preview/sessions/${sessionId}`);
      setDetail(r.data || null);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("admin.demoActivityLog.errors.detailFailed")));
    } finally {
      setDetailLoading(false);
    }
  }, [t]);

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  // Cosmetic block (Feb 2026) — clone a historical demo session into
  // a fresh active demo session and land directly inside it.
  const onClone = useCallback(async (sessionId) => {
    setCloningId(sessionId);
    try {
      await cloneSession(sessionId);
      toast.success(t("admin.demoActivityLog.cloneSuccess"));
      // Same UX as starting a fresh session: full reload so AuthContext
      // re-fetches /auth/me with the new demo token.
      window.location.assign("/app");
    } catch (err) {
      toast.error(extractErrorMessage(err, t("admin.demoActivityLog.cloneFailed")));
      setCloningId(null);
    }
  }, [cloneSession, t]);

  const totals = useMemo(() => {
    let actions = 0;
    let blocked = 0;
    let comms = 0;
    let sandbox = 0;
    for (const s of sessions) {
      actions += Number(s.actions_count || 0);
      blocked += Number(s.side_effects_blocked || 0);
      comms += Number(s.comms_intercepted || 0);
      sandbox += Number(s.sandbox_writes_count || 0);
    }
    return { actions, blocked, comms, sandbox, sessions: sessions.length };
  }, [sessions]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="demo-activity-log-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="demo-activity-log-title">
            <Eye className="w-6 h-6 text-purple-700" />
            {t("admin.demoActivityLog.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {t("admin.demoActivityLog.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 disabled:opacity-50"
          data-testid="demo-activity-log-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t("admin.demoActivityLog.refresh")}
        </button>
      </header>

      {/* Summary tiles */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="demo-activity-log-summary">
        <SummaryTile label={t("admin.demoActivityLog.summary.sessions")} value={totals.sessions} accent="emerald" />
        <SummaryTile label={t("admin.demoActivityLog.summary.actions")} value={totals.actions} accent="slate" />
        <SummaryTile label={t("admin.demoActivityLog.summary.blocked")} value={totals.blocked} accent="amber" />
        <SummaryTile label={t("admin.demoActivityLog.summary.comms")} value={totals.comms} accent="purple" />
        <SummaryTile label={t("admin.demoActivityLog.summary.sandbox")} value={totals.sandbox} accent="emerald" />
      </section>

      {/* Sessions list */}
      <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="demo-activity-log-sessions">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-semibold text-slate-800">{t("admin.demoActivityLog.sessionsTitle")}</h2>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="demo-activity-log-only-sandbox-toggle">
              <input
                type="checkbox"
                checked={onlySandbox}
                onChange={(e) => setOnlySandbox(e.target.checked)}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="demo-activity-log-only-sandbox-checkbox"
              />
              <span>{t("admin.demoActivityLog.onlySandbox")}</span>
            </label>
            <span className="text-xs text-slate-500">{t("admin.demoActivityLog.sessionsHint")}</span>
          </div>
        </div>
        {sessions.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="demo-activity-log-empty">
            {loading ? t("admin.demoActivityLog.loading") : t("admin.demoActivityLog.emptySessions")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-start px-4 py-2 font-medium">{t("admin.demoActivityLog.col.started")}</th>
                  <th className="text-start px-4 py-2 font-medium">{t("admin.demoActivityLog.col.actor")}</th>
                  <th className="text-start px-4 py-2 font-medium">{t("admin.demoActivityLog.col.identity")}</th>
                  <th className="text-start px-4 py-2 font-medium">{t("admin.demoActivityLog.col.vertical")}</th>
                  <th className="text-end px-4 py-2 font-medium">{t("admin.demoActivityLog.col.actions")}</th>
                  <th className="text-end px-4 py-2 font-medium">{t("admin.demoActivityLog.col.blocked")}</th>
                  <th className="text-end px-4 py-2 font-medium">{t("admin.demoActivityLog.col.intercepted")}</th>
                  <th className="text-end px-4 py-2 font-medium">{t("admin.demoActivityLog.col.sandbox")}</th>
                  <th className="text-start px-4 py-2 font-medium">{t("admin.demoActivityLog.col.state")}</th>
                  <th className="px-2 py-2"> </th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const ended = !!s.ended_at;
                  return (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`demo-session-row-${s.id}`}>
                      <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{fmtDate(s.started_at)}</td>
                      <td className="px-4 py-2 text-slate-700 font-mono text-xs">{(s.actor_admin_id || "").slice(0, 12)}</td>
                      <td className="px-4 py-2 text-slate-700 font-mono text-xs">{s.demo_identity_id}</td>
                      <td className="px-4 py-2 text-slate-600">{s.vertical_key}</td>
                      <td className="px-4 py-2 text-end tabular-nums">{s.actions_count || 0}</td>
                      <td className={`px-4 py-2 text-end tabular-nums ${s.side_effects_blocked > 0 ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{s.side_effects_blocked || 0}</td>
                      <td className={`px-4 py-2 text-end tabular-nums ${s.comms_intercepted > 0 ? "text-purple-700 font-semibold" : "text-slate-500"}`}>{s.comms_intercepted || 0}</td>
                      <td className={`px-4 py-2 text-end tabular-nums ${s.sandbox_writes_count > 0 ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>{s.sandbox_writes_count || 0}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${ended ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-800"}`}>
                          {ended ? t("admin.demoActivityLog.state.ended") : t("admin.demoActivityLog.state.active")}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-end whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openDetail(s.id)}
                          className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
                          data-testid={`demo-session-view-${s.id}`}
                        >
                          {t("admin.demoActivityLog.view")}
                        </button>
                        <button
                          type="button"
                          onClick={() => onClone(s.id)}
                          disabled={cloningId === s.id}
                          title={t("admin.demoActivityLog.cloneTooltip")}
                          className="ms-3 inline-flex items-center gap-1 text-purple-700 hover:text-purple-900 text-xs font-semibold disabled:opacity-50"
                          data-testid={`demo-session-clone-${s.id}`}
                        >
                          <Copy className="w-3 h-3" />
                          {cloningId === s.id
                            ? t("admin.demoActivityLog.cloning")
                            : t("admin.demoActivityLog.clone")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Intercepted comms feed */}
      <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="demo-activity-log-comms">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-slate-800">{t("admin.demoActivityLog.commsTitle")}</h2>
          <div className="flex items-center gap-2">
            <label htmlFor="channelFilter" className="text-xs text-slate-500">
              {t("admin.demoActivityLog.filter.channel")}
            </label>
            <select
              id="channelFilter"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs"
              data-testid="demo-activity-log-channel-filter"
            >
              <option value="">{t("admin.demoActivityLog.filter.all")}</option>
              <option value="email">{channelLabel(t, "email")}</option>
              <option value="sms">{channelLabel(t, "sms")}</option>
              <option value="whatsapp">{channelLabel(t, "whatsapp")}</option>
              <option value="push">{channelLabel(t, "push")}</option>
            </select>
          </div>
        </div>
        {intercepted.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="demo-comms-empty">
            {loading ? t("admin.demoActivityLog.loading") : t("admin.demoActivityLog.emptyComms")}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {intercepted.map((c) => {
              const Icon = CHANNEL_ICONS[c.channel] || ShieldAlert;
              return (
                <li key={c.id} className="px-5 py-3 flex items-start gap-3" data-testid={`demo-comm-row-${c.id}`}>
                  <Icon className="w-4 h-4 mt-1 text-purple-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{channelLabel(t, c.channel)}</span>
                      <span className="text-xs text-slate-500">{fmtDate(c.created_at)}</span>
                      <span className="inline-flex items-center rounded-full bg-purple-100 text-purple-800 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                        {t("admin.demoActivityLog.intercepted")}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      <span className="font-mono">{c.payload?.to || "—"}</span>
                      {c.payload?.subject && (
                        <span className="ms-2">· <em>{c.payload.subject}</em></span>
                      )}
                    </div>
                    {c.session_id && (
                      <button
                        type="button"
                        onClick={() => openDetail(c.session_id)}
                        className="text-[11px] text-emerald-700 hover:text-emerald-900 mt-1"
                      >
                        {t("admin.demoActivityLog.viewSession")} ({(c.session_id || "").slice(0, 8)}…)
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Detail drawer */}
      {selectedId && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 flex items-start justify-end"
          onClick={closeDetail}
          data-testid="demo-activity-log-drawer"
        >
          <div
            className="w-full sm:w-[640px] h-full bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">{t("admin.demoActivityLog.detail.title")}</h3>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-full p-1.5 hover:bg-slate-100"
                data-testid="demo-activity-log-drawer-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {detailLoading && (
                <div className="text-sm text-slate-500">{t("admin.demoActivityLog.loading")}</div>
              )}
              {detail && (
                <>
                  <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <Field label={t("admin.demoActivityLog.detail.id")} value={detail.session?.id} mono />
                      <Field label={t("admin.demoActivityLog.detail.actor")} value={detail.session?.actor_admin_id} mono />
                      {detail.session?.cloned_from_session_id && (
                        <Field
                          label={t("admin.demoActivityLog.clonedFromLabel")}
                          value={detail.session.cloned_from_session_id}
                          mono
                        />
                      )}
                      <Field label={t("admin.demoActivityLog.detail.identity")} value={detail.identity?.name || detail.session?.demo_identity_id} />
                      <Field label={t("admin.demoActivityLog.detail.vertical")} value={detail.session?.vertical_key} />
                      <Field label={t("admin.demoActivityLog.detail.started")} value={fmtDate(detail.session?.started_at)} />
                      <Field label={t("admin.demoActivityLog.detail.expires")} value={fmtDate(detail.session?.expires_at)} />
                      <Field label={t("admin.demoActivityLog.detail.ended")} value={fmtDate(detail.session?.ended_at)} />
                      <Field label={t("admin.demoActivityLog.detail.lastSeen")} value={fmtDate(detail.session?.last_seen_at)} />
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm text-slate-800 mb-2 flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-amber-600" />
                      {t("admin.demoActivityLog.detail.blockedTitle")} ({(detail.session?.blocked_events || []).length})
                    </h4>
                    {(detail.session?.blocked_events || []).length === 0 ? (
                      <div className="text-xs text-slate-500">{t("admin.demoActivityLog.detail.noBlocked")}</div>
                    ) : (
                      <ul className="space-y-1.5">
                        {detail.session.blocked_events.slice().reverse().map((ev, idx) => (
                          <li key={idx} className="text-xs bg-amber-50 border border-amber-100 rounded-md px-3 py-2 font-mono text-amber-900">
                            <span className="me-2 font-bold">{ev.method}</span>
                            <span className="me-2">{ev.path}</span>
                            <span className="text-amber-700">[{ev.reason}]</span>
                            <span className="ms-2 text-slate-500">{fmtDate(ev.ts)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* D3-A — Sandbox writes audit trail. */}
                  <div>
                    <h4 className="font-semibold text-sm text-slate-800 mb-2 flex items-center gap-2">
                      <Eye className="w-4 h-4 text-emerald-700" />
                      {t("admin.demoActivityLog.detail.sandboxTitle")} ({(detail.session?.sandbox_writes || []).length})
                    </h4>
                    {(detail.session?.sandbox_writes || []).length === 0 ? (
                      <div className="text-xs text-slate-500">{t("admin.demoActivityLog.detail.noSandbox")}</div>
                    ) : (
                      <ul className="space-y-1.5">
                        {detail.session.sandbox_writes.slice().reverse().map((ev, idx) => (
                          <li key={idx} className="text-xs bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2 font-mono text-emerald-900">
                            <span className="me-2 font-bold">{ev.action}</span>
                            <span className="text-emerald-700">{ev.collection}</span>
                            <span className="ms-2 text-slate-500">{fmtDate(ev.ts)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm text-slate-800 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-purple-700" />
                      {t("admin.demoActivityLog.detail.commsTitle")} ({(detail.intercepted_comms || []).length})
                    </h4>
                    {(detail.intercepted_comms || []).length === 0 ? (
                      <div className="text-xs text-slate-500">{t("admin.demoActivityLog.detail.noComms")}</div>
                    ) : (
                      <ul className="space-y-2">
                        {detail.intercepted_comms.map((c) => {
                          const Icon = CHANNEL_ICONS[c.channel] || ShieldAlert;
                          return (
                            <li key={c.id} className="text-xs bg-purple-50 border border-purple-100 rounded-md px-3 py-2">
                              <div className="flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5 text-purple-700" />
                                <span className="font-semibold text-purple-900">{channelLabel(t, c.channel)}</span>
                                <span className="text-slate-500 ms-auto">{fmtDate(c.created_at)}</span>
                              </div>
                              <div className="mt-1 font-mono text-slate-700">{c.payload?.to}</div>
                              {c.payload?.subject && (
                                <div className="text-slate-600 italic">{c.payload.subject}</div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent }) => {
  const palette = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    purple: "border-purple-200 bg-purple-50 text-purple-900",
    slate: "border-slate-200 bg-white text-slate-900",
  }[accent] || "border-slate-200 bg-white text-slate-900";
  return (
    <div className={`rounded-xl border ${palette} p-4`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-slate-800 ${mono ? "font-mono break-all" : ""}`}>{value || "—"}</div>
  </div>
);

export default DemoActivityLog;
